import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import BlogContent from "@/components/blog/BlogContent";
import { Button } from "@/components/ui/button";
import AuthorCard from "@/components/blog/AuthorCard";
import { useSEO } from "@/hooks/useSEO";

const BlogPost = () => {
  const { slug } = useParams();

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select(`
          *,
          author:profiles(*)
        `)
        .eq("slug", slug)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  useSEO({
    title: post ? `${post.title} | Loch Ness Story` : "Blog Post | Loch Ness Story",
    description: post?.excerpt || "Read this article on Loch Ness Story.",
    canonical: slug ? `/blog/${slug}` : undefined,
    ogImage: post?.image_url || "/lovable-uploads/74638626-9ce3-4018-94b3-ec8980da02b9.png",
    ogType: "article",
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <p className="text-white">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-900">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4 text-white">Post not found</h1>
            <p className="text-gray-400 mb-8">The blog post you're looking for doesn't exist.</p>
            <Link to="/">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                Return Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      <main className="container mx-auto px-4 py-16">
        <div className="flex flex-col lg:flex-row gap-8">
          <BlogContent
            title={post.title}
            content={post.content}
            image={post.image_url || "/placeholder.svg"}
            categories={["Blog"]}
          />
          <div className="lg:w-80">
            <AuthorCard 
              author={post.author?.full_name || post.author?.username || 'Anonymous Researcher'} 
              date={new Date(post.published_at || post.created_at).toLocaleDateString()}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default BlogPost;